/* eslint-disable import-extensions/file-extension-required */
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import FetchMediaTokenQuery, {
	type graphqlHelpersFetchMediaTokenQuery$data,
	type graphqlHelpersFetchMediaTokenQuery$variables,
} from '../MediaProvider/__generated__/graphqlHelpersFetchMediaTokenQuery.graphql';
import FetchJiraMediaTokenQuery, {
	type graphqlHelpersFetchJiraMediaTokenQuery$data,
	type graphqlHelpersFetchJiraMediaTokenQuery$variables,
} from '../MediaProvider/__generated__/graphqlHelpersFetchJiraMediaTokenQuery.graphql';

export const preloadJiraMediaToken = async (issueAri: string) => {
	const response = await query<
		graphqlHelpersFetchJiraMediaTokenQuery$data,
		graphqlHelpersFetchJiraMediaTokenQuery$variables
	>({
		query: FetchJiraMediaTokenQuery,
		variables: { issueAri, maxTokenLength: 65536 },
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const mediaReadToken = response?.data?.jira?.issueById?.mediaReadToken;

		const { jiraMediaConfigRecorder } = await import(
			/* webpackChunkName: "loadable-confluencessr-utilities-objectrecorder" */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
		);

		// Only record valid credentials. If clientId or endpointUrl are missing
		// (e.g. the @optIn backend directive is not enabled, or the issue has no
		// media), writing empty strings would produce a truthy object that bypasses
		// the `if (!recordedAuth)` guard in ssrMediaHelpers and causes the media
		// client to attempt rendering with invalid auth — skipping the set entirely
		// is the safe default so SSR falls back to client-side auth gracefully.
		const clientId = mediaReadToken?.clientId;
		const baseUrl = mediaReadToken?.endpointUrl;
		const token = mediaReadToken?.tokensWithFiles?.edges?.[0]?.node?.token;

		if (clientId && baseUrl) {
			jiraMediaConfigRecorder.set({
				clientId,
				baseUrl,
				token: token || '',
			});
		}
	}

	return response;
};

export const preloadMediaToken = async (contentId: string) => {
	const response = await query<
		graphqlHelpersFetchMediaTokenQuery$data,
		graphqlHelpersFetchMediaTokenQuery$variables
	>({
		query: FetchMediaTokenQuery,
		variables: {
			contentId,
			status: ['current', 'draft', 'archived'],
		},
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const { mediaConfigRecorder } = await import(
			/* webpackChunkName: "loadable-confluencessr-utilities-objectrecorder" */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
		);
		mediaConfigRecorder.set({
			clientId: response?.data?.mediaConfiguration?.clientId || '',
			baseUrl: response?.data?.mediaConfiguration?.fileStoreUrl || '',
			token: response?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token || '',
		});
	}

	return response;
};
